import CopyableId from "@/components/common/CopyableId";
import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ClipboardList, FilePlus2, Clock } from "lucide-react";
import PageHeader from "@/components/layout/PageHeader";
import DataTable from "@/components/common/DataTable";
import WorkflowBadge from "@/pages/onboarding/WorkflowBadge";
import { STATUS_LABELS, canCreateRequest } from "@/pages/onboarding/constants";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export default function RequestList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const role = user?.role_code || "viewer";

  const status = searchParams.get("status") || "";
  const scope = searchParams.get("scope") || "";
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(15);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  // The status param can be a comma-separated list (e.g., assigned,visit_in_progress)
  const statuses = useMemo(() => (status ? status.split(",") : []), [status]);
  const statusLabel = useMemo(() => {
    if (statuses.length === 0) return "All Statuses";
    if (statuses.length === 1) return STATUS_LABELS[statuses[0]] || statuses[0];
    return statuses.map((s) => STATUS_LABELS[s] || s).join(" + ");
  }, [statuses]);

  async function load() {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      if (statuses.length === 1) params.status = statuses[0];
      if (statuses.length > 1) params.statuses = statuses.join(",");
      if (search) params.q = search;
      if (scope === "mine") params.mine = true;
      if (scope === "involved") params.involved = true;
      // role-default scoping (applies only to stage queues, not to mine/involved views)
      if (!status && !scope && (role === "hospital_coordinator" || role === "hah_physician" || role === "physician" || role === "hah_coordinator" || role === "care_coordinator" || role === "home_care_team")) {
        params.role_scope = role;
      }
      const res = await api.get("/onboarding/requests", { params });
      setItems(res.data.items);
      setTotal(res.data.total);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [page, status, scope]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { const t = setTimeout(() => { setPage(1); load(); }, 300); return () => clearTimeout(t); }, [search]);

  function setStatusFilter(v) {
    const next = new URLSearchParams(searchParams);
    if (v === "all") next.delete("status");
    else next.set("status", v);
    setSearchParams(next);
    setPage(1);
  }

  const columns = [
    {
      key: "ref_number", label: "Ref #", sortable: false,
      render: (r) => <span className="font-mono text-xs text-[#0B7E8C] font-medium">{r.ref_number}</span>,
    },
    {
      key: "patient", label: "Patient",
      render: (r) => (
        <div className="min-w-0">
          <div className="font-medium text-[#1F2937] truncate">{r.patient?.first_name} {r.patient?.last_name}</div>
          <div className="text-xs text-[#6B7280] truncate"><CopyableId value={r.patient?.mrn} kind="MRN" /> · {r.patient?.gender || "—"}</div>
        </div>
      ),
    },
    {
      key: "diagnosis", label: "Primary diagnosis",
      render: (r) => <span className="text-sm text-[#1F2937]">{r.clinical?.primary_diagnosis || "—"}</span>,
    },
    {
      key: "priority", label: "Priority",
      render: (r) => (
        <span className="text-xs capitalize text-[#6B7280]">{r.service?.priority || "—"}</span>
      ),
    },
    {
      key: "status", label: "Status",
      render: (r) => (
        <div className="flex items-center gap-1.5">
          <WorkflowBadge status={r.status} />
          {r.readmit_eligibility?.eligible && (
            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-[#D9770614] text-[#B45309] border border-[#D9770633]" data-testid={`ob-readmit-badge-${r.id}`}>READMIT 48h</span>
          )}
          {r.is_readmission && (
            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-[#D9770614] text-[#B45309] border border-[#D9770633]" data-testid={`ob-readmission-badge-${r.id}`}>Readmission</span>
          )}
        </div>
      ),
    },
    {
      key: "updated_at", label: "Last update",
      render: (r) => (
        <div className="flex items-center gap-1 text-xs text-[#6B7280]">
          <Clock className="w-3 h-3" />
          {r.updated_at ? new Date(r.updated_at).toLocaleString() : "—"}
        </div>
      ),
    },
  ];

  return (
    <div data-testid="onboarding-list-page">
      <PageHeader
        title="Onboarding Requests"
        subtitle={`Showing: ${statusLabel}${scope === "mine" ? " · Mine" : scope === "involved" ? " · My Submissions" : ""}`}
        actions={
          canCreateRequest(role) && (
            <Button data-testid="ob-list-new" onClick={() => navigate("/onboarding/new")} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white">
              <FilePlus2 className="w-4 h-4 mr-1.5" /> New Request
            </Button>
          )
        }
      />

      <DataTable
        testId="ob-table"
        columns={columns}
        data={items}
        total={total}
        page={page}
        pageSize={pageSize}
        loading={loading}
        searchValue={search}
        onSearchChange={setSearch}
        onPageChange={setPage}
        onRowClick={(r) => navigate(`/onboarding/requests/${r.id}`)}
        exportFilename="onboarding-requests.csv"
        filters={
          <Select value={statuses.length === 1 ? statuses[0] : statuses.length > 1 ? "_multi" : "all"} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[220px] h-9 border-[#E5E7EB]" data-testid="ob-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {Object.entries(STATUS_LABELS).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        }
      />
    </div>
  );
}
